import {customElement, property} from "lit/decorators.js";
import {LoadAfterConfirmationEvents} from "../../page/elements/loadAfterConfirmation";
import {resolve} from "../../container";
import {Resolution} from "../../common/resolution";
import {GoogleMapsFactory} from "./googleMapsFactory";
import {ManagingResources} from "../../common/lifetime";
import {UnLitElement} from "../../common/elements";
import {COLOR_PRIMARY, COLOR_WHITE} from "./colors";
import {GoogleMapsRunner} from "./runner";
import {EopCustomClusterRenderer} from "./clusterRenderer";
import {
    type EopGoogleMapsInteractiveMarker,
    EopGoogleMapsPoi,
    EopGoogleMapsPolygon,
    EopGoogleMapsPolyline,
    type EopGoogleMapsVisual,
    GOOGLE_MAPS_OPEN_POI_OVERLAY
} from "./poi";
import {ColorSchemeService} from "../../common/colorSchemeService";
import {EventBus} from "../../common/eventBus";
import type {Maybe, Nullable} from "../../bootstrap/common/types";
import {EopErrorHandler} from "../../common/errorHandler";

@customElement("eop-google-map")
export class EopGoogleMap extends ManagingResources(UnLitElement) {

    private mapCenter: Maybe<google.maps.LatLng>;
    private map: google.maps.Map;
    private visuals: EopGoogleMapsVisual[];
    private interactiveMarkers: EopGoogleMapsInteractiveMarker[];
    private initialized: boolean;
    private googleMapsElement: Nullable<HTMLElement>;
    private confirmationAbortController: AbortController;

    @property({attribute: "zoom", type: Number})
    public zoom: number = 10;
    @property({attribute: "center-by-pois", type: Boolean})
    private centerByPOIs: boolean = false;
    @property({attribute: "center-lat", type: Number})
    private centerLat: number;
    @property({attribute: "center-lng", type: Number})
    private centerLng: number;
    @property({attribute: "map-type"})
    public mapType: string = "roadmap";
    @property({attribute: "clustering", type: Boolean})
    public useClustering: boolean = false;


    public constructor(
        private loadAfterConfirmationEvents: LoadAfterConfirmationEvents = resolve(LoadAfterConfirmationEvents),
        private googleMapsRunner: GoogleMapsRunner = resolve(GoogleMapsRunner),
        private resolution: Resolution = resolve(Resolution),
        private googleMapsFactory: GoogleMapsFactory = resolve(GoogleMapsFactory),
        private colorSchemeService: ColorSchemeService = resolve(ColorSchemeService),
        private eventBus: EventBus = resolve(EventBus),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();

        this.initialized = false;
        this.visuals = [];
        this.interactiveMarkers = [];
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.confirmationAbortController = this.loadAfterConfirmationEvents.onConfirmation("googlemaps", () => {
            return this.googleMapsRunner.run()
                .then(() => this.initMap())
                .catch(e => this.errorHandler.report(e));
        });
    }

    public disconnectedCallback(): void {
        this.confirmationAbortController.abort();
        super.disconnectedCallback();
    }

    private initMap(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        this.googleMapsElement = this.querySelector<HTMLElement>(".google-maps-element")!;

        const pois = Array.from(this.querySelectorAll<EopGoogleMapsPoi>("eop-google-maps-poi"));
        this.interactiveMarkers = [...pois];

        const polylines = Array.from(this.querySelectorAll<EopGoogleMapsPolyline>("eop-google-maps-polyline"));
        const polygons = Array.from(this.querySelectorAll<EopGoogleMapsPolygon>("eop-google-maps-polygon"));
        this.visuals = [...polylines, ...polygons];

        if (this.centerByPOIs) {
            this.mapCenter = this.determineMapCenter([...this.visuals, ...this.interactiveMarkers]);
        } else {
            this.mapCenter = this.googleMapsFactory.createLatLng(this.centerLat, this.centerLng);
        }

        this.map = this.googleMapsFactory.createMap(this.googleMapsElement, {
            center: this.mapCenter,
            zoom: this.zoom,
            tilt: 0,
            mapTypeId: this.mapType,
            mapTypeControl: true,
            scaleControl: true,
            renderingType: this.googleMapsFactory.createRenderingTypeFor(this.mapType),
            colorScheme: this.googleMapsFactory.createColorScheme(this.colorSchemeService.getColorScheme()),
            mapId: "DEMO_MAP_ID" // this is needed for advanced markers (see: https://developers.google.com/maps/documentation/javascript/map-ids/mapid-over#features-available)
        });

        this.resolution.onWindowResize(() => {
            if (this.mapCenter) {
                this.map.setCenter(this.mapCenter);
            }
        }, this);

        this.visuals.forEach(drawable => drawable.renderOnMap(this.map));
        this.interactiveMarkers.forEach(clusterable => clusterable.renderOnMap(this.map));

        this.map.addListener("click", () => {
            this.closeAllPoiOverlays();
        });
        this.eventBus.on(GOOGLE_MAPS_OPEN_POI_OVERLAY, () => {
            this.closeAllPoiOverlays();
        });

        this.googleMapsElement.firstElementChild!.prepend(this.querySelector(".pois")!);

        if (this.useClustering) {
            this.googleMapsFactory.createMarkerClusterer(this.map, this.interactiveMarkers.flatMap(p => p.markerElement()),
                new EopCustomClusterRenderer(this.getComputedColor(COLOR_WHITE), this.getComputedColor(COLOR_PRIMARY)));
        }
    }

    private closeAllPoiOverlays(): void {
        this.interactiveMarkers.forEach(p => p.closeOverlay());
    }

    private determineMapCenter(drawables: EopGoogleMapsVisual[]): Maybe<google.maps.LatLng> {
        if (drawables.length === 0) {
            return undefined;
        }
        const mostNorth = Math.max(...drawables.map(d => d.center().lat));
        const mostSouth = Math.min(...drawables.map(d => d.center().lat));
        const mostEast = Math.max(...drawables.map(d => d.center().lng));
        const mostWest = Math.min(...drawables.map(d => d.center().lng));
        return this.googleMapsFactory.createLatLng((mostNorth + mostSouth) / 2, (mostEast + mostWest) / 2);
    }

    private getComputedColor(propertyName: string): string {
        return getComputedStyle(this).getPropertyValue(propertyName);
    }
}